import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";

import {
  hexToRgb,
  isValidHex,
  normalizeHex,
  readableTextColor,
  rgbToHex,
} from "@utils/productClassification";
import { FILE_PROXY_URL } from "@libs/apiUrl";

const HUE_STOPS = ["#FF0000", "#FFFF00", "#00FF00", "#00FFFF", "#0000FF", "#FF00FF", "#FF0000"];

const isCrossOrigin = (src: string) => {
  if (!src || src.startsWith("data:") || src.startsWith("blob:")) return false;
  try {
    return new URL(src, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
};

// S3 버킷에 CORS 설정이 없어 브라우저가 픽셀을 읽지 못한다.
// 교차 출처 이미지는 CORS 헤더를 붙여주는 백엔드 프록시를 거쳐 불러온다.
// data:/blob:/같은 출처는 그대로 쓴다 (crossOrigin을 붙이면 로드 자체가 실패).
const toPixelReadableSrc = (src: string) =>
  isCrossOrigin(src) ? `${FILE_PROXY_URL}?url=${encodeURIComponent(src)}` : src;

// canvas 픽셀을 읽으려면 교차 출처 이미지에 crossOrigin이 필요하다.
// 프록시 URL도 백엔드(:8080)라 프론트(:3002)와는 교차 출처다.
// data:/blob:/같은 출처에 붙이면 오히려 로드가 실패하므로 그때만 뺀다.
const crossOriginFor = (src: string) => (isCrossOrigin(src) ? "anonymous" : undefined);

type Hsv = { h: number; s: number; v: number };

const hsvToHex = ({ h, s, v }: Hsv) => {
  const sector = (h % 360) / 60;
  const chroma = v * s;
  const x = chroma * (1 - Math.abs((sector % 2) - 1));
  const offset = v - chroma;
  const [r, g, b] = [
    [chroma, x, 0], [x, chroma, 0], [0, chroma, x],
    [0, x, chroma], [x, 0, chroma], [chroma, 0, x],
  ][Math.floor(sector)];
  return rgbToHex({ r: (r + offset) * 255, g: (g + offset) * 255, b: (b + offset) * 255 });
};

const rgbToHsv = (rgb: { r: number; g: number; b: number }, previous: Hsv) => {
  const [r, g, b] = [rgb.r / 255, rgb.g / 255, rgb.b / 255];
  const max = Math.max(r, g, b);
  const delta = max - Math.min(r, g, b);
  let h = previous.h;
  if (delta > 0) {
    h = 60 * (max === r ? ((g - b) / delta + 6) % 6 : max === g ? (b - r) / delta + 2 : (r - g) / delta + 4);
  }
  return { h, s: max === 0 ? previous.s : delta / max, v: max };
};

type Props = {
  images?: string[];
  value: string;
  onChange: (hex: string) => void;
};

const ColorPickerPanel = ({ images = [], value, onChange }: Props) => {
  const [hsv, setHsv] = useState(() => isValidHex(value)
    ? rgbToHsv(hexToRgb(value), { h: 0, s: 1, v: 1 })
    : { h: 0, s: 1, v: 1 });

  useEffect(() => {
    if (!isValidHex(value)) return;
    setHsv((previous) => normalizeHex(value).toUpperCase() === hsvToHex(previous).toUpperCase()
      ? previous
      : rgbToHsv(hexToRgb(value), previous));
  }, [value]);

  const handleHsvChange = (next: Hsv) => {
    setHsv(next);
    onChange(hsvToHex(next));
  };

  const [imageIndex, setImageIndex] = useState(0);
  const [isEyedropping, setIsEyedropping] = useState(false);
  const [marker, setMarker] = useState<{ x: number; y: number } | null>(null);
  const [imageError, setImageError] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const activeImage = images[imageIndex];
  const rgb = useMemo(() => (isValidHex(value) ? hexToRgb(value) : { r: 0, g: 0, b: 0 }), [value]);

  // 이미지가 지워지거나 줄어들면 인덱스를 되돌린다.
  useEffect(() => {
    if (imageIndex > images.length - 1) setImageIndex(0);
  }, [images.length, imageIndex]);

  useEffect(() => {
    setMarker(null);
    setImageError(false);
  }, [activeImage]);

  const readPixel = (event: React.MouseEvent<HTMLImageElement>) => {
    const image = imageRef.current;
    const canvas = canvasRef.current;
    if (!image || !canvas || !image.naturalWidth) return;

    const bounds = image.getBoundingClientRect();
    const offsetX = event.clientX - bounds.left;
    const offsetY = event.clientY - bounds.top;

    // object-fit: contain 기준으로 실제 그려진 영역을 계산한다.
    const scale = Math.min(bounds.width / image.naturalWidth, bounds.height / image.naturalHeight);
    const drawnWidth = image.naturalWidth * scale;
    const drawnHeight = image.naturalHeight * scale;
    const padX = (bounds.width - drawnWidth) / 2;
    const padY = (bounds.height - drawnHeight) / 2;

    // 여백(레터박스)을 누르면 픽셀이 없으므로 무시한다.
    if (
      offsetX < padX ||
      offsetY < padY ||
      offsetX > padX + drawnWidth ||
      offsetY > padY + drawnHeight
    ) {
      return;
    }

    const naturalX = Math.floor((offsetX - padX) / scale);
    const naturalY = Math.floor((offsetY - padY) / scale);

    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return;
    context.drawImage(image, 0, 0);

    try {
      const [r, g, b] = Array.from(context.getImageData(naturalX, naturalY, 1, 1).data);
      setMarker({ x: (offsetX / bounds.width) * 100, y: (offsetY / bounds.height) * 100 });
      onChange(rgbToHex({ r, g, b }));
    } catch {
      // 이미지 호스트가 CORS 헤더를 주지 않으면 canvas가 오염되어 픽셀을 읽을 수 없다.
      setImageError(true);
      setIsEyedropping(false);
    }
  };

  const handleHexInput = (raw: string) => {
    const next = raw.startsWith("#") || raw === "" ? raw : `#${raw}`;
    onChange(next.toUpperCase());
  };

  const handleChannelInput = (channel: "r" | "g" | "b", raw: string) => {
    if (!isValidHex(value)) return;
    onChange(rgbToHex({ ...rgb, [channel]: raw === "" ? 0 : Number(raw) }));
  };

  const swatchColor = isValidHex(value) ? normalizeHex(value) : "transparent";

  return (
    <Panel onClick={(event) => event.stopPropagation()}>
      <PanelTitle>사진에서 색상 선택</PanelTitle>

      <Stage>
        {activeImage ? (
          <>
            <StageImage
              ref={imageRef}
              src={toPixelReadableSrc(activeImage)}
              alt="색상 추출용 상품 이미지"
              crossOrigin={crossOriginFor(toPixelReadableSrc(activeImage))}
              draggable={false}
              isEyedropping={isEyedropping}
              onClick={isEyedropping ? readPixel : undefined}
              onLoad={() => setImageError(false)}
              onError={() => setImageError(true)}
            />
            <EyedropperButton
              type="button"
              active={isEyedropping}
              onClick={() => setIsEyedropping((on) => !on)}
            >
              ◎ {isEyedropping ? "선택 중… (사진 클릭)" : "사진에서 색상 선택"}
            </EyedropperButton>
            {marker && <Marker style={{ left: `${marker.x}%`, top: `${marker.y}%` }} />}
          </>
        ) : (
          <StageEmpty>
            상품 사진을 먼저 등록하면
            <br />
            사진에서 색상을 추출할 수 있습니다.
          </StageEmpty>
        )}
      </Stage>

      {images.length > 1 && (
        <Thumbnails>
          {images.map((image, index) => (
            <Thumbnail
              key={image}
              type="button"
              selected={index === imageIndex}
              onClick={() => setImageIndex(index)}
            >
              <img src={image} alt={`상품 이미지 ${index + 1}`} />
            </Thumbnail>
          ))}
        </Thumbnails>
      )}

      <PickerControl>
        색조
        <HueSlider
          type="range" min={0} max={360} value={hsv.h}
          aria-label="색조 슬라이더"
          onChange={(event) => handleHsvChange({ ...hsv, h: Number(event.target.value) })}
        />
      </PickerControl>
      <PickerControl>
        채도 {Math.round(hsv.s * 100)}%
        <HueSlider
          type="range" min={0} max={100} value={hsv.s * 100}
          aria-label="채도 슬라이더"
          style={{ background: `linear-gradient(to right, ${hsvToHex({ ...hsv, s: 0 })}, ${hsvToHex({ ...hsv, s: 1 })})` }}
          onChange={(event) => handleHsvChange({ ...hsv, s: Number(event.target.value) / 100 })}
        />
      </PickerControl>
      <PickerControl>
        밝기 {Math.round(hsv.v * 100)}%
        <HueSlider
          type="range" min={0} max={100} value={hsv.v * 100}
          aria-label="밝기 슬라이더"
          style={{ background: `linear-gradient(to right, #000000, ${hsvToHex({ ...hsv, v: 1 })})` }}
          onChange={(event) => handleHsvChange({ ...hsv, v: Number(event.target.value) / 100 })}
        />
      </PickerControl>

      <HexRow>
        <Swatch
          style={{
            background: swatchColor,
            color: isValidHex(value) ? readableTextColor(value) : "#999999",
          }}
          aria-label="선택한 색상 미리보기"
        >
          {isValidHex(value) ? "" : "?"}
        </Swatch>
        <HexInput
          value={value}
          onChange={(event) => handleHexInput(event.target.value)}
          placeholder="#RRGGBB"
          maxLength={7}
          aria-label="HEX 값"
          invalid={Boolean(value) && !isValidHex(value)}
        />
        <NativePicker
          type="color"
          aria-label="색상 선택"
          value={isValidHex(value) ? normalizeHex(value) : "#000000"}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
        />
      </HexRow>

      <RgbRow>
        {(["r", "g", "b"] as const).map((channel) => (
          <RgbField key={channel}>
            <span>{channel.toUpperCase()}</span>
            <input
              type="number"
              min={0}
              max={255}
              value={isValidHex(value) ? rgb[channel] : ""}
              disabled={!isValidHex(value)}
              onChange={(event) => handleChannelInput(channel, event.target.value)}
              aria-label={`${channel.toUpperCase()} 값`}
            />
          </RgbField>
        ))}
      </RgbRow>

      {imageError ? (
        <HelpText role="alert" tone="error">
          이미지를 불러오지 못해 사진에서 색상을 추출할 수 없습니다. HEX 또는 RGB로 입력해주세요.
        </HelpText>
      ) : (
        <HelpText>피커·HEX·RGB 값이 원형 스와치에 즉시 동기화됩니다.</HelpText>
      )}

      <HiddenCanvas ref={canvasRef} />
    </Panel>
  );
};

export default ColorPickerPanel;

const Panel = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "0.8rem",
  width: "100%",
});

const PanelTitle = styled.strong({ fontSize: "1.3rem" });

const Stage = styled.div({
  position: "relative",
  width: "100%",
  height: "16rem",
  background: "#ececec",
  overflow: "hidden",
});

const StageImage = styled.img<{ isEyedropping: boolean }>(({ isEyedropping }) => ({
  width: "100%",
  height: "100%",
  objectFit: "contain",
  display: "block",
  cursor: isEyedropping ? "crosshair" : "default",
  userSelect: "none",
}));

const StageEmpty = styled.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  textAlign: "center",
  fontSize: "1.2rem",
  lineHeight: 1.6,
  color: "#999999",
});

const EyedropperButton = styled.button<{ active: boolean }>(({ active }) => ({
  position: "absolute",
  top: "0.8rem",
  left: "0.8rem",
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4rem",
  padding: "0.7rem 1.1rem",
  border: 0,
  borderRadius: "2rem",
  fontSize: "1.2rem",
  fontWeight: 600,
  color: "#fff",
  background: active ? "#00B585" : "rgba(17, 17, 17, 0.72)",
  cursor: "pointer",
}));

const Marker = styled.div({
  position: "absolute",
  width: "1.8rem",
  height: "1.8rem",
  marginLeft: "-0.9rem",
  marginTop: "-0.9rem",
  borderRadius: "50%",
  border: "3px solid #fff",
  boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.35)",
  pointerEvents: "none",
});

const Thumbnails = styled.div({ display: "flex", gap: "0.5rem", flexWrap: "wrap" });

const Thumbnail = styled.button<{ selected: boolean }>(({ selected }) => ({
  width: "3.6rem",
  height: "3.6rem",
  padding: 0,
  overflow: "hidden",
  cursor: "pointer",
  border: selected ? "2px solid #00B585" : "1px solid #ccc",
  background: "none",
  img: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
}));

const PickerControl = styled.label({ display: "flex", flexDirection: "column", gap: "0.8rem", fontSize: "1.3rem" });

const HueSlider = styled.input({
  width: "100%",
  height: "1.4rem",
  appearance: "none",
  borderRadius: "0.7rem",
  cursor: "pointer",
  background: `linear-gradient(to right, ${HUE_STOPS.join(", ")})`,
  "&::-webkit-slider-thumb": {
    appearance: "none",
    width: "1.6rem",
    height: "1.6rem",
    borderRadius: "50%",
    background: "#fff",
    border: "2px solid rgba(0, 0, 0, 0.3)",
    cursor: "pointer",
  },
});

const HexRow = styled.div({
  display: "grid",
  gridTemplateColumns: "3.8rem 1fr 4.4rem",
  alignItems: "center",
  gap: "0.8rem",
});

const Swatch = styled.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "3.8rem",
  height: "3.8rem",
  borderRadius: "50%",
  border: "1px solid #ccc",
  fontSize: "1.4rem",
  fontWeight: 700,
});

const HexInput = styled.input<{ invalid: boolean }>(({ invalid }) => ({
  height: "3.8rem",
  padding: "0 1rem",
  fontSize: "1.3rem",
  border: `1px solid ${invalid ? "#FF4808" : "#e0e0e0"}`,
  outlineColor: invalid ? "#FF4808" : "#00B585",
}));

const NativePicker = styled.input({
  width: "4.4rem",
  height: "3.8rem",
  padding: 0,
  border: "1px solid #e0e0e0",
  background: "none",
  cursor: "pointer",
});

const RgbRow = styled.div({ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.8rem" });

const RgbField = styled.label({
  display: "flex",
  flexDirection: "column",
  gap: "0.3rem",
  fontSize: "1.1rem",
  color: "#999999",
  input: {
    width: "100%",
    height: "3.6rem",
    padding: "0 0.8rem",
    fontSize: "1.3rem",
    color: "#111",
    border: "1px solid #e0e0e0",
    "&:disabled": { background: "#f4f4f4" },
  },
});

const HelpText = styled.p<{ tone?: string }>(({ tone }) => ({
  margin: 0,
  fontSize: "1.1rem",
  lineHeight: 1.5,
  color: tone === "error" ? "#FF4808" : "#999999",
}));

const HiddenCanvas = styled.canvas({ display: "none" });
